import React, { useContext } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Callout,
  Combobox,
  Box as FormActions,
  Box as FormContainer,
  Box as FormFields,
  Input,
} from '@node-space/ui-components'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { contactAtom } from 'pages/Contacts/atoms/contactAtoms'
import { ContactCard } from 'pages/Contacts/components/ContactCard'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { useChannelPaymentForm } from './hooks/useChannelPaymentForm'

export const ChannelPaymentDetails = () => {
  const { t } = useTranslation()
  const [walletAction] = useContext(WalletActionContext)
  const contact = useAtomValue(contactAtom)

  const {
    activeWallet,
    currenciesOptions,
    cryptoCurrenciesOptionsMapped,
    errors,
    formFields,
    hasCurrencyMismatch,
    isFetchingCurrencies,
    isFetchingWallets,
    isPending,
    requestError,
    walletError,
    activeWalletOptions,
    handleWalletOpen,
    handleWalletClose,
    handleSubmit,
    handleOnChangeWallet,
    onSubmit,
    setSearchWalletQuery,
    setValue,
    watch,
  } = useChannelPaymentForm()

  return (
    <FormContainer tag="form" data-testid="channel-details" onSubmit={handleSubmit(onSubmit)}>
      <ContactCard
        title={t('payments.channelPayments.createChannelFor')}
        showAvatar={false}
        name={contact?.name}
      />

      <FormFields width="full" flexCol gapY={16} paddingT={16}>
        {requestError?.show && <RequestErrorCallout requestError={requestError} />}

        <Combobox
          {...formFields.walletId}
          id="from-wallet"
          testId="from-wallet"
          items={activeWalletOptions}
          label={t('formFields.wallet')}
          loading={isFetchingWallets}
          loadingInline={isFetchingWallets}
          onChange={handleOnChangeWallet}
          onInputChange={setSearchWalletQuery}
          disabled={!!walletAction?.meta?.sourceWallet?.lsid}
          defaultValue={activeWallet?.lsid ?? walletAction?.meta?.sourceWallet?.lsid}
          errorText={
            errors?.walletId?.message || (walletError && t('wallets.send.sendFromWalletRequired'))
          }
          onOpen={isOpen => (isOpen ? handleWalletOpen() : handleWalletClose())}
          modal
        />

        <Combobox
          {...formFields.displayCurrency}
          id="displayCurrency"
          testId="display-currency"
          key={`display_${watch('displayCurrency')}`}
          label={t('formFields.displayCurrency')}
          items={currenciesOptions}
          defaultValue={watch('displayCurrency')}
          onChange={value => {
            setValue('displayCurrency', value, { shouldValidate: true, shouldDirty: true })
          }}
          loading={isFetchingCurrencies}
          loadingInline={isFetchingCurrencies}
          modal
        />

        {hasCurrencyMismatch && (
          <Callout
            testId="display-currency-warning"
            variant="warning"
            GhostButton={
              <Button
                testId="reset-currency"
                variant="ghost"
                noPadding
                className="underline"
                onClick={() => {
                  setValue('displayCurrency', activeWallet?.currency?.code, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }}
              >
                {t('ResetTheDisplayCurrency')}
              </Button>
            }
          >
            {t('displayCurrencyNotice')}
          </Callout>
        )}

        <Combobox
          {...formFields.payCurrency}
          id="payCurrency"
          testId="pay-currency"
          label={t('formFields.payCurrency')}
          items={cryptoCurrenciesOptionsMapped}
          defaultValue={watch('payCurrency')}
          key={`pay_${watch('payCurrency')}`} // key is a workaround until the ui-components is fixed to accept updates from a parent component
          onChange={value => {
            setValue('payCurrency', value, { shouldValidate: true, shouldDirty: true })
          }}
          loading={isFetchingCurrencies}
          loadingInline={isFetchingCurrencies}
          modal
        />

        <Input
          {...formFields.reference}
          onChange={e =>
            setValue('reference', e.target.value, { shouldValidate: true, shouldDirty: true })
          }
          testId="reference"
          placeholderText={t('uniqueReference')}
          errorText={errors?.reference?.message}
          error={!!errors?.reference}
        />

        <FormActions flex justify="between">
          <Button testId="cancel" variant="secondary" onClick={walletAction?.onClose}>
            {t('cancel')}
          </Button>

          <Button
            testId="create-channel"
            type="submit"
            variant="primary"
            loading={isPending}
            disabled={!!Object.keys(errors)?.length}
          >
            {t('payments.channelPayments.createChannel')}
          </Button>
        </FormActions>
      </FormFields>
    </FormContainer>
  )
}
